import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { ClearanceVertex } from '@/lib/clearance/types';
import { CLEARANCE_VIEW_STATE_DEFAULT, type ClearanceViewState } from '@/lib/clearance/viewMode';

/**
 * Dialog-time envelope preview; null whenever the dialog is closed.
 *
 * Same shape and lifecycle as `stationVolumePreviewAtom`: the dialog writes it
 * on every relevant change and clears it on unmount, and the 3D component
 * renders nothing when it is null. Keeping it here rather than inside the
 * dialog is what lets a component INSIDE the r3f canvas react to a form that
 * lives outside it.
 *
 * `from` / `to` are null while the user has left the station range untouched,
 * meaning "the whole alignment" — the same convention `clearancePayload` uses
 * when it omits the two fields from the mutation.
 */
export const clearancePreviewAtom = atom<{
  key: string;
  vertices: ClearanceVertex[];
  verticalOffsetM: number;
  from: number | null;
  to: number | null;
} | null>(null);

/**
 * The run's swept envelope in 3D. Default ON and per-layer, exactly like
 * `stationBandsVisibleAtom`: "what did it violate?" is unanswerable without the
 * surface it violated, and the envelope is also what connects the chart to the
 * ground — it carries the per-bin verdict, the brush selection and the hover
 * that the flat corridor plates used to. (Those plates had a switch of their
 * own; it went with them, because two toggles for one object is a question the
 * reader should never have been asked.) Turned off it costs nothing — the
 * component returns null before it builds any geometry.
 */
export const clearanceEnvelopeVisibleAtom = atomFamily((_layerId: string) => atom(true));

export const clearanceViewModeStorageKey = (layerId: string) => `@actegon.gis.clearance.viewMode.${layerId}`;

/**
 * The "which points violated" render mode for one clearance layer, together
 * with the snapshot of the settings the dock took over to show it
 * (`ClearanceViewState`, defined once in `lib/clearance/viewMode.ts` — ruling
 * P4). Keyed by the DERIVED layer (`dock.layerId`, the cloud carrying class
 * 13), like the two visibility toggles above.
 *
 * Persisted, and deliberately so. The take-over is written through
 * `layerSettingsAtom`, which is itself localStorage-backed: the repainted
 * cloud outlives a page reload whether we like it or not. Keeping the snapshot
 * that undoes it in the SAME medium is what stops a reload mid-mode from
 * leaving a magenta cloud with nothing left in the app that remembers what it
 * looked like — `ClearanceDock` releases any surviving hold on its next mount.
 * `getOnInit` so that first read is the stored value and not the default.
 */
export const clearanceViewModeAtom = atomFamily((layerId: string) =>
  atomWithStorage<ClearanceViewState>(
    clearanceViewModeStorageKey(layerId),
    CLEARANCE_VIEW_STATE_DEFAULT,
    createJSONStorage<ClearanceViewState>(() => localStorage),
    { getOnInit: true },
  ),
);
